// A elegibilidade ao Simples Nacional — os critérios da LC 123/2006, como DADO,
// e a análise como função pura. Prova própria em `app/tests/simples-nacional.mjs`.
//
// ---------------------------------------------------------------------------
// AS PERGUNTAS SÃO DADO, NÃO CÓDIGO
// ---------------------------------------------------------------------------
// O mesmo motivo de `irp_perguntas` (000065): texto de enunciado dentro de um
// componente é texto que ninguém revê. Aqui elas ainda moram no módulo — e não
// no banco — porque o enunciado cita ARTIGO de lei complementar, não valor de
// temporada: muda quando a LC 123 mudar, e aí é deploy de todo modo. É a regra
// do corte da 000108 aplicada a texto em vez de número.
//
// Os três valores que a LC fixa (R$ 4,8 milhões, R$ 3,6 milhões, R$ 360 mil)
// vêm de `simplesNacional.ts` e não são reescritos aqui — senão a tela e o
// cálculo passariam a ter duas versões do mesmo limite.
//
// ---------------------------------------------------------------------------
// "7 CRITÉRIOS LEGAIS" — E SÃO NOVE
// ---------------------------------------------------------------------------
// A tela inicial da origem e o manual dela anunciam "7 critérios legais"; o
// questionário tem 9 perguntas, e o manual chega a listar sete assuntos que não
// batem com nenhuma delas ("importação", "regime especial", que não existem no
// código). Número escrito à mão ao lado de uma lista é número que descola dela
// na primeira edição — como os "22 títulos" do Calendário, que eram 20.
//
// Aqui a tela conta `PERGUNTAS.length`, e não há número escrito em lugar nenhum.
//
// ---------------------------------------------------------------------------
// BLOQUEIO x ALERTA, E O QUE CADA UM SIGNIFICA
// ---------------------------------------------------------------------------
// Bloqueio impede a opção (a empresa não pode ser do Simples). Alerta não
// impede: ou é regularizável antes da opção (débito fiscal), ou é uma consequência
// a considerar (o sublimite tira ICMS/ISS do DAS, mas a empresa continua no
// Simples). Misturar os dois faria a ferramenta dizer "não pode" a quem pode.

import { LIMITE_ME, SUBLIMITE, TETO_ANUAL, emReais } from "./simplesNacional";

export interface Opcao {
  valor: string;
  rotulo: string;
}

export interface Pergunta {
  id: string;
  texto: string;
  baseLegal: string;
  opcoes: Opcao[];
  /** Uma pergunta que só aparece quando esta for respondida com `quando`. */
  filha?: { id: string; texto: string; quando: string; opcoes: Opcao[] };
}

const SIM_NAO: Opcao[] = [
  { valor: "nao", rotulo: "Não" },
  { valor: "sim", rotulo: "Sim" },
];

export const PERGUNTAS: Pergunta[] = [
  {
    id: "faturamento",
    texto: `A receita bruta anual da empresa ultrapassa ${emReais(TETO_ANUAL)}?`,
    baseLegal: "LC 123/2006, art. 3º, II",
    opcoes: [
      { valor: "nao", rotulo: "Não, está dentro do limite" },
      { valor: "sim", rotulo: "Sim, ultrapassa" },
    ],
  },
  {
    id: "socio_pj",
    texto: "A empresa possui pessoa jurídica como sócia?",
    baseLegal: "LC 123/2006, art. 3º, §4º, I",
    opcoes: SIM_NAO,
  },
  {
    id: "participacao_outra",
    texto: "Algum sócio participa de outra empresa com mais de 10% do capital?",
    baseLegal: "LC 123/2006, art. 3º, §4º, IV",
    opcoes: SIM_NAO,
    filha: {
      id: "soma_faturamento",
      texto: `A soma do faturamento das empresas ultrapassa ${emReais(TETO_ANUAL)}?`,
      quando: "sim",
      opcoes: [
        { valor: "nao", rotulo: "Não" },
        { valor: "sim", rotulo: "Sim" },
        // O terceiro estado existe porque ele é o comum: quem está preenchendo
        // costuma não ter o faturamento da outra empresa à mão. Sem ele, a
        // pessoa marca "não" para poder avançar, e a tela responde "pode optar"
        // sobre uma informação que ninguém tem.
        { valor: "nao_sei", rotulo: "Não sei / preciso conferir" },
      ],
    },
  },
  {
    id: "debitos",
    texto: "A empresa possui débitos com INSS ou fazendas públicas (federal, estadual, municipal)?",
    baseLegal: "LC 123/2006, art. 17, V",
    opcoes: [
      { valor: "nao", rotulo: "Não possui débitos" },
      { valor: "sim", rotulo: "Sim, possui débitos" },
    ],
  },
  {
    id: "atividade_vedada",
    texto: "A empresa exerce alguma atividade vedada ao Simples Nacional (ex.: instituição financeira, factoring)?",
    baseLegal: "LC 123/2006, art. 17",
    opcoes: SIM_NAO,
  },
  {
    id: "filial_exterior",
    texto: "A empresa possui filial ou sucursal no exterior?",
    baseLegal: "LC 123/2006, art. 3º, §4º, XI",
    opcoes: SIM_NAO,
  },
  {
    id: "forma_societaria",
    texto: "A empresa é constituída como sociedade por ações (S.A.) ou cooperativa (exceto de consumo)?",
    baseLegal: "LC 123/2006, art. 3º, §4º, X",
    opcoes: SIM_NAO,
  },
  {
    id: "sublimite",
    texto: `A receita bruta anual estimada ultrapassará o sublimite de ICMS/ISS (${emReais(SUBLIMITE)})?`,
    baseLegal: "LC 123/2006, art. 13-A e art. 19",
    opcoes: [
      { valor: "nao", rotulo: "Não, será inferior ao sublimite" },
      { valor: "sim", rotulo: "Sim, será superior ao sublimite" },
    ],
  },
  {
    id: "porte",
    texto: "Qual o faturamento bruto anual estimado da empresa?",
    baseLegal: "LC 123/2006, art. 3º, I e II",
    opcoes: [
      { valor: "me", rotulo: `Até ${emReais(LIMITE_ME)}` },
      { valor: "epp", rotulo: `De ${emReais(LIMITE_ME + 0.01)} até ${emReais(TETO_ANUAL)}` },
    ],
  },
];

export type Situacao = "pode" | "restricao" | "nao_pode";

export interface Motivo {
  texto: string;
  baseLegal: string;
  tipo: "bloqueio" | "alerta";
}

export interface Resultado {
  situacao: Situacao;
  motivos: Motivo[];
  porte: "ME" | "EPP" | null;
  sublimite: "dentro" | "acima" | null;
  /** Quantas perguntas ainda não foram respondidas — a tela não conclui sem zero. */
  faltamResponder: number;
}

/**
 * A análise.
 *
 * Um bloqueio derruba para "não pode"; só alertas dão "com restrições"; nenhum
 * dos dois dá "pode". A ORDEM importa para a leitura: os motivos saem na ordem
 * das perguntas, e não agrupados por tipo, porque quem responde acompanha a
 * própria sequência.
 */
export function analisar(respostas: Record<string, string>): Resultado {
  const motivos: Motivo[] = [];
  const bloqueio = (texto: string, baseLegal: string) => motivos.push({ texto, baseLegal, tipo: "bloqueio" });
  const alerta = (texto: string, baseLegal: string) => motivos.push({ texto, baseLegal, tipo: "alerta" });

  if (respostas.faturamento === "sim") {
    bloqueio(`Receita bruta anual acima de ${emReais(TETO_ANUAL)}`, "LC 123/2006, art. 3º, II");
  }
  if (respostas.socio_pj === "sim") {
    bloqueio("Possui pessoa jurídica como sócia", "LC 123/2006, art. 3º, §4º, I");
  }

  if (respostas.participacao_outra === "sim") {
    // Três desfechos, e o do meio é o mais comum. A origem tinha dois: "sim"
    // bloqueava e QUALQUER outra coisa — inclusive a pergunta em branco —
    // virava alerta. Aqui "não sei" tem texto próprio, porque a diferença entre
    // "não ultrapassa" e "não sei se ultrapassa" é a diferença entre uma
    // resposta e uma tarefa.
    if (respostas.soma_faturamento === "sim") {
      bloqueio(
        `Sócio participa de outra empresa e a soma dos faturamentos ultrapassa ${emReais(TETO_ANUAL)}`,
        "LC 123/2006, art. 3º, §4º, IV",
      );
    } else if (respostas.soma_faturamento === "nao") {
      alerta(
        "Sócio participa de outra empresa. A soma dos faturamentos está dentro do limite, mas convém guardar a apuração.",
        "LC 123/2006, art. 3º, §4º, IV",
      );
    } else {
      alerta(
        `Sócio participa de outra empresa e a soma dos faturamentos ainda não foi conferida — se passar de ${emReais(TETO_ANUAL)}, a opção fica vedada.`,
        "LC 123/2006, art. 3º, §4º, IV",
      );
    }
  }

  if (respostas.debitos === "sim") {
    alerta(
      "Possui débitos com INSS ou fazendas públicas — a regularização é condição para a opção, e pode ser feita antes do prazo.",
      "LC 123/2006, art. 17, V",
    );
  }
  if (respostas.atividade_vedada === "sim") {
    bloqueio("Exerce atividade vedada ao Simples Nacional", "LC 123/2006, art. 17");
  }
  if (respostas.filial_exterior === "sim") {
    bloqueio("Possui filial ou sucursal no exterior", "LC 123/2006, art. 3º, §4º, XI");
  }
  if (respostas.forma_societaria === "sim") {
    bloqueio("Forma societária vedada (S.A. ou cooperativa)", "LC 123/2006, art. 3º, §4º, X");
  }

  const sublimite = respostas.sublimite === "sim" ? "acima" : respostas.sublimite === "nao" ? "dentro" : null;
  if (sublimite === "acima") {
    alerta(
      `Receita acima do sublimite de ${emReais(SUBLIMITE)} — ICMS e ISS passam a ser recolhidos fora do DAS. A empresa continua no Simples Nacional.`,
      "LC 123/2006, art. 13-A e art. 19",
    );
  }

  const porte = respostas.porte === "me" ? "ME" : respostas.porte === "epp" ? "EPP" : null;

  // Quantas das perguntas de topo ainda estão em branco. A filha não conta: ela
  // só existe num dos ramos, e contá-la faria a barra de progresso andar para
  // trás quando alguém trocasse "não" por "sim".
  const faltamResponder = PERGUNTAS.filter((p) => !respostas[p.id]).length;

  const temBloqueio = motivos.some((m) => m.tipo === "bloqueio");
  const temAlerta = motivos.some((m) => m.tipo === "alerta");

  return {
    situacao: temBloqueio ? "nao_pode" : temAlerta ? "restricao" : "pode",
    motivos,
    porte,
    sublimite,
    faltamResponder,
  };
}

export const ROTULO_DA_SITUACAO: Record<Situacao, string> = {
  pode: "Pode optar pelo Simples Nacional",
  restricao: "Pode optar, com pontos de atenção",
  nao_pode: "Não pode optar pelo Simples Nacional",
};
